import type { CascadingMenuItem } from '@UI/New/CascadingDropdown'
import type {
  TaskTrackerCrmLeadDealNode,
  TaskTrackerCrmLeadFunnelNode,
  TaskTrackerCrmLeadTreeItem,
} from './taskTrackerCrmLeadSticker.types'
import {
  formatCrmLeadTreeDealLabel,
  formatCrmLeadTreeFunnelLabel,
  formatCrmLeadTreeLeadLabel,
} from './taskTrackerCrmLeadSticker.utils'

export type CrmLeadStickerPickPayload = {
  leadFunnelId: number
  leadId: number
  funnelId?: number | null
  leadDisplayName: string
}

type BuildCrmLeadCascadingMenuItemsParams = {
  leads: TaskTrackerCrmLeadTreeItem[]
  onPick: (payload: CrmLeadStickerPickPayload) => void
  /** Сделки ON — каскад до сделки; OFF — клик по воронке. */
  multipleDealsEnabled?: boolean
}

const buildDealItem = (
  lead: TaskTrackerCrmLeadTreeItem,
  deal: TaskTrackerCrmLeadDealNode,
  onPick: (payload: CrmLeadStickerPickPayload) => void,
  funnelId?: number | null,
): CascadingMenuItem => {
  const leadDisplayName = formatCrmLeadTreeLeadLabel(lead)

  return {
    key: `deal:${deal.leadFunnelId}`,
    label: formatCrmLeadTreeDealLabel(deal),
    onClick: () => {
      onPick({
        leadFunnelId: deal.leadFunnelId,
        leadId: lead.leadId,
        funnelId: funnelId ?? null,
        leadDisplayName,
      })
    },
  }
}

const buildFunnelLeaf = (
  lead: TaskTrackerCrmLeadTreeItem,
  funnel: TaskTrackerCrmLeadFunnelNode,
  deal: TaskTrackerCrmLeadDealNode,
  onPick: (payload: CrmLeadStickerPickPayload) => void,
): CascadingMenuItem => {
  const leadDisplayName = formatCrmLeadTreeLeadLabel(lead)

  return {
    key: `funnel:${lead.leadId}:${funnel.funnelId}`,
    label: formatCrmLeadTreeFunnelLabel(funnel),
    onClick: () => {
      onPick({
        leadFunnelId: deal.leadFunnelId,
        leadId: lead.leadId,
        funnelId: funnel.funnelId,
        leadDisplayName,
      })
    },
  }
}

const buildFunnelItem = (
  lead: TaskTrackerCrmLeadTreeItem,
  funnel: TaskTrackerCrmLeadFunnelNode,
  onPick: (payload: CrmLeadStickerPickPayload) => void,
  multipleDealsEnabled: boolean,
): CascadingMenuItem => {
  const deals = funnel.deals

  if (deals.length === 0) {
    return {
      key: `funnel:${lead.leadId}:${funnel.funnelId}`,
      label: formatCrmLeadTreeFunnelLabel(funnel),
      disabled: true,
    }
  }

  // Сделки OFF: выбор по воронке (берём первую/единственную связку).
  if (!multipleDealsEnabled) {
    return buildFunnelLeaf(lead, funnel, deals[0], onPick)
  }

  if (deals.length === 1) {
    return buildFunnelLeaf(lead, funnel, deals[0], onPick)
  }

  return {
    key: `funnel:${lead.leadId}:${funnel.funnelId}`,
    label: formatCrmLeadTreeFunnelLabel(funnel),
    children: deals.map((deal) =>
      buildDealItem(lead, deal, onPick, funnel.funnelId),
    ),
  }
}

/** Лид → воронка → (сделка, если multiple deals ON и их несколько). */
export const buildCrmLeadCascadingMenuItems = ({
  leads,
  onPick,
  multipleDealsEnabled = false,
}: BuildCrmLeadCascadingMenuItemsParams): CascadingMenuItem[] =>
  leads.map((lead) => {
    const funnels = lead.funnels.filter((funnel) => funnel.deals.length > 0)

    if (funnels.length === 0) {
      return {
        key: `lead:${lead.leadId}`,
        label: formatCrmLeadTreeLeadLabel(lead),
        disabled: true,
      }
    }

    return {
      key: `lead:${lead.leadId}`,
      label: formatCrmLeadTreeLeadLabel(lead),
      children: funnels.map((funnel) =>
        buildFunnelItem(lead, funnel, onPick, multipleDealsEnabled),
      ),
    }
  })
